import React from 'react';
import { Button, Menu } from '@mantine/core';
import ExcelJS from 'exceljs';
import { Trade, Portfolio } from '../../types';
import { IconDownload } from '@tabler/icons-react';

// helper to turn anything into a number, or 0 if it isn't numeric
function toNumericId(id:any): number {
  const n = Number(id);
  return Number.isNaN(n) ? 0 : n;
}

interface Props {
  trades: Trade[];
  portfolios: Portfolio[];
  hideAvailableShares?: boolean;
  asMenu?: boolean;
}

const TradeAllocationExcel: React.FC<Props> = ({
  trades,
  portfolios,
  hideAvailableShares = false,
  asMenu = false
}) => {
  const generateExcel = async () => {
    if (trades.length > 0) {
      trades.sort((a, b) => (a.execId ?? toNumericId(a.id)) - (b.execId ?? toNumericId(b.id)));
    }
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Allocations');

    // Constants for better maintainability
    const HEADER_ROW = 3;
    const DATA_START_ROW = 4;
    const PORTFOLIO_COL_WIDTH = 3; // columns per portfolio
    const BASE_COLS = 10;

    // Style constants
    const HEADER_STYLE = {
      font: { bold: true, color: { argb: 'FFFFFFFF' } },
      fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4ead5b' } },
      // alignment: { wrapText: true, vertical: 'middle' as const, horizontal: 'center' as const }
    };

    const BORDER_STYLE = {
      top: { style: 'thin' as const },
      left: { style: 'thin' as const },
      bottom: { style: 'thin' as const },
      right: { style: 'thin' as const }
    };

    // Map initial shares per portfolio+security
    const initialSharesMap: Record<string, number> = {};
    portfolios.forEach((p) => {
      [...(p.position?.local_position || []), ...(p.position?.foreign_position || [])]
        .forEach((pos) => {
          if (pos.firebase_key) {
            initialSharesMap[`${p.id}_${pos.firebase_key}`] = pos.quantity ?? 0;
          }
        });
    });

    // 1. Static cash row (row 1)
    ws.addRow([
      ...Array(BASE_COLS).fill(''),
      ...portfolios.flatMap((p) => ['', p.position?.cash ?? 0, '']),
      ''
    ]);
    ws.getCell('B1').value = 'Cash Available';
    ws.getCell('B1').font = { bold: true };

    // Portfolio IDs row (row 2)
    ws.addRow([
      ...Array(9).fill(''),
      ...portfolios.flatMap((p) => ['', Number(p.id), '']),
      ''
    ]);

    // 2. Header row (row 3)
    const header = [
      'Trade #', 'Broker', 'Share', 'Share Name', 'FirebaseKey',
      'Time', 'Side', 'Qty', 'Price (fils)', 'Total (KD)',
      ...portfolios.flatMap((p) => [`${p.name_en}`, 'Cash (KD)', 'Available Shares']),
      '', 'Total','', 'Diff',
    ];
    ws.addRow(header);

    // Setup header styling and filters
    ws.autoFilter = { from: { row: HEADER_ROW, column: 1 }, to: { row: HEADER_ROW, column: 9 } };
    ws.getColumn(5).hidden = true; // hide FirebaseKey
    ws.getRow(HEADER_ROW).height = 60;

    // Track last share row per portfolio+security
    const prevShareRow: Record<string, number> = {};

    // 3. Data rows
    trades.forEach((trade, idx) => {
      const r = idx + DATA_START_ROW;
      const tradeID = Number.isSafeInteger(trade.execId) || !String(trade.id).startsWith(`${trade.accountId}_`)
        ? trade.execId?.toString() ?? trade.id
        : String(trade.id).slice(trade.accountId.length + 1); // GCC exec IDs overflow a JS number

      // Base trade data
      const base = [
        tradeID , trade.dealer, trade.symbolCode, trade.symbolDesc,
        trade.firebaseKey, trade.tradeTime, trade.side,
        trade.filledQty, trade.price, { formula: `(H${r}*I${r})/1000` }
      ];

      // Portfolio allocations
      const cells: any[] = [];
      const allocRefs: string[] = [];

      portfolios.forEach((p, j) => {
        const allocCol = BASE_COLS + 1 + j * PORTFOLIO_COL_WIDTH;
        const cashCol = allocCol + 1;
        const availCol = allocCol + 2;

        const allocAddr = `${ws.getColumn(allocCol).letter}${r}`;
        const sideAddr = `G${r}`; // Side column
        const priceAddr = `I${r}`; // Price column
        allocRefs.push(allocAddr);

        // Cash formula
        const prevCashAddr = `${ws.getColumn(cashCol).letter}${r === DATA_START_ROW ? 1 : r - 1}`;
        const cashFormula = `IF(${allocAddr}="",${prevCashAddr},IF(${sideAddr}="BUY",${prevCashAddr}-${allocAddr}*${priceAddr}/1000,${prevCashAddr}+${allocAddr}*${priceAddr}/1000))`;

        // Available shares formula
        const key = `${p.id}_${trade.firebaseKey}`;
        const initialQty = initialSharesMap[key] ?? 0;
        const availColLetter = ws.getColumn(availCol).letter;

        let availFormula: string;
        if (prevShareRow[key]) {
          const prevAddr = `${availColLetter}${prevShareRow[key]}`;
          availFormula = `IF(${allocAddr}="",${prevAddr},IF(${sideAddr}="BUY",${prevAddr}+${allocAddr},${prevAddr}-${allocAddr}))`;
        } else {
          availFormula = `IF(${allocAddr}="",${initialQty},IF(${sideAddr}="BUY",${initialQty}+${allocAddr},${initialQty}-${allocAddr}))`;
        }

        cells.push(null, { formula: cashFormula }, { formula: availFormula });
        prevShareRow[key] = r;
      });

      // Remaining quantity formula
      const remFormula = {
        formula: `IF(SUM(${allocRefs.join(',')})=0,0-H${r},SUM(${allocRefs.join(',')})-H${r})`
      };
      const totalFormula = {
        formula: `IF(SUM(${allocRefs.join(',')})=0,0,SUM(${allocRefs.join(',')}))`
      };

      ws.addRow([...base, ...cells, null,totalFormula, null, remFormula]);
    });

    // 4. Apply styling and formatting
    const lastDataRow = trades.length + HEADER_ROW;
    const remColIdx = header.length;
    const remColLetter = ws.getColumn(remColIdx).letter;

    // Portfolio-specific formatting
    portfolios.forEach((_, j) => {
      const baseCol = BASE_COLS + 1 + j * PORTFOLIO_COL_WIDTH;
      const cashCol = baseCol + 1;
      const availCol = baseCol + 2;

      // Column widths
      ws.getColumn(baseCol).width = 25;
      ws.getColumn(cashCol).width = 15;
      ws.getColumn(availCol).width = 12;

      if (hideAvailableShares) {
        ws.getColumn(availCol).hidden = true;
      }

      // Cash column formatting
      ws.getColumn(cashCol).eachCell((c) => {
        c.numFmt = '#,##0.000;(#,##0.000)';
        // c.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      // Conditional formatting
      const cashLetter = ws.getColumn(cashCol).letter;
      const availLetter = ws.getColumn(availCol).letter;

      ws.addConditionalFormatting({
        ref: `${cashLetter}${DATA_START_ROW}:${cashLetter}${lastDataRow}`,
        rules: [{
          type: 'cellIs',
          operator: 'lessThan',
          formulae: ['0'],
          style: {
            fill: {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFC7CE' },
              bgColor: { argb: 'FFFFC7CE' }
            }
          },
          priority: j + 1
        }]
      });

      ws.addConditionalFormatting({
        ref: `${availLetter}${DATA_START_ROW}:${availLetter}${lastDataRow}`,
        rules: [{
          type: 'cellIs',
          operator: 'lessThan',
          formulae: ['0'],
          style: {
            fill: {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFC7CE' },
              bgColor: { argb: 'FFFFC7CE' }
            }
          },
          priority: portfolios.length + j + 1
        }]
      });
    });

    // Remaining quantity conditional formatting
    ws.addConditionalFormatting({
      ref: `${remColLetter}${DATA_START_ROW}:${remColLetter}${lastDataRow}`,
      rules: [
        {
          type: 'cellIs',
          operator: 'lessThan',
          formulae: ['0'],
          style: {
            fill: {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFC7CE' },
              bgColor: { argb: 'FFFFC7CE' }
            }
          },
          priority: 1
        },
        {
          type: 'cellIs',
          operator: 'greaterThan',
          formulae: ['0'],
          style: {
            fill: {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFC7CE' },
              bgColor: { argb: 'FFFFC7CE' }
            }
          },
          priority: 1
        },
        {
          type: 'cellIs',
          operator: 'equal',
          formulae: ['0'],
          style: {
            fill: {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFC6EFCE' },
              bgColor: { argb: 'FFC6EFCE' }
            }
          },
          priority: 2
        }
      ]
    });

    // Row-level conditional formatting (fully allocated trades)
    const cashRefs = portfolios.map((_, j) =>
      `$${ws.getColumn(BASE_COLS + 2 + j * PORTFOLIO_COL_WIDTH).letter}${DATA_START_ROW}`
    ).join(',');
    const availRefs = portfolios.map((_, j) =>
      `$${ws.getColumn(BASE_COLS + 3 + j * PORTFOLIO_COL_WIDTH).letter}${DATA_START_ROW}`
    ).join(',');

    ws.addConditionalFormatting({
      ref: `A${DATA_START_ROW}:${remColLetter}${lastDataRow}`,
      rules: [{
        type: 'expression',
        formulae: [`AND($${remColLetter}${DATA_START_ROW}=0,MIN(${cashRefs})>=0,MIN(${availRefs})>=0)`],
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFC6EFCE' },
            bgColor: { argb: 'FFC6EFCE' }
          }
        },
        priority: 0
      }]
    });

    // Column styling
    // ws.getColumn(remColIdx - 1).fill = {
    //   type: 'pattern',
    //   pattern: 'solid',
    //   fgColor: { argb: 'FFFFFFFF' }
    // };
    ws.getColumn(remColIdx).width = 20;
    ws.getColumn(remColIdx - 2).width = 20;
    ws.getColumn(2).width = 35;
    ws.getColumn(4).width = 15;

    // Apply header styles
    ws.getRow(2).eachCell((c) => {
      const col = Number(c.col);
      if (col > BASE_COLS) {
        Object.assign(c, HEADER_STYLE);
      }
    });

    ws.getRow(HEADER_ROW).eachCell((c) => {
      Object.assign(c, HEADER_STYLE);
    });

    ws.getColumn(remColIdx - 1).width = 3;
    ws.getColumn(remColIdx - 1).eachCell((c) => {
      const row = Number(c.row);
      if (row > HEADER_ROW) {
        Object.assign(c, HEADER_STYLE)
      } else {
        c.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFFFF' }
        };
      }
    });
    ws.getColumn(remColIdx - 3).width = 3;
    ws.getColumn(remColIdx - 3).eachCell((c) => {
      const row = Number(c.row);
      if (row > HEADER_ROW) {
        Object.assign(c, HEADER_STYLE)
      } else {
        c.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFFFF' }
        };
      }
    });

    // Apply general formatting
    ws.eachRow((row) => row.eachCell((cell) => {
      cell.alignment = { wrapText: true ,vertical: 'middle', horizontal: 'center' };
      const col = Number(cell.col);
      const rowNum = Number(cell.row);

      if ((col === 8 || col === 10 || col > 10) && rowNum !== 2) {
        cell.numFmt = '#,##0;(#,##0)';
      }
    }));

    // Apply bold to all cells
    ws.eachRow((row) => row.eachCell((c) => (c.font = { ...c.font, bold: true })));

    ws.eachRow((row) => {
      let rowNum = Number(row.number);
      if (rowNum != HEADER_ROW) {
        row.height = 15;
      }
      
    });

    // Apply borders to all cells
    for (let rowIndex = 1; rowIndex <= ws.rowCount; rowIndex++) {
      for (let colIndex = 1; colIndex <= header.length; colIndex++) {
        const cell = ws.getCell(rowIndex, colIndex);
        cell.border = BORDER_STYLE;

        if (colIndex >= 11 && colIndex % 3 === 2 && rowIndex > HEADER_ROW) {
          cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
          cell.numFmt = '#,##0;(#,##0)';
        }
        if (colIndex >= 12 && colIndex % 3 === 0 && colIndex <= header.length - 4) {
          cell.numFmt = '#,##0.000;(#,##0.000)';
        }
      }
    }

    // Export
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Generate filename with first trade date
    let filename = 'allocation';
    if (trades.length > 0 && trades[0].tradeDate) {
      const firstTradeDate = trades[0].tradeDate.toDate();
      const dd = String(firstTradeDate.getDate()).padStart(2, '0');
      const mm = String(firstTradeDate.getMonth() + 1).padStart(2, '0');
      const yyyy = firstTradeDate.getFullYear();
      filename = `allocation_${dd}_${mm}_${yyyy}`;
    }

    a.download = `${filename}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buttonText = `Download Allocations ${hideAvailableShares ? '(Hiding Available Shares)' : ''}`;

  if (asMenu) {
    return (
      <Menu.Item onClick={generateExcel} leftSection={<IconDownload size="1rem" />}>
        {buttonText}
      </Menu.Item>
    );
  }

  return <Button onClick={generateExcel}>{buttonText}</Button>;
};

export default TradeAllocationExcel;